import { useState, useRef, useCallback } from "react";

// Custom hook for tooltip with stable positioning and proper hide behavior
export const useTooltip = () => {
  const [tooltipData, setTooltipData] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const hideTimeoutRef = useRef(null);

  const showTooltip = useCallback((data, event) => {
    // Clear any pending hide timeout
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }

    setTooltipData(data);
    // Get element's bounding rect for stable positioning
    const target = event.currentTarget;
    const rect = target.getBoundingClientRect();
    // Position tooltip centered above/below the element
    setTooltipPosition({
      x: rect.left + rect.width / 2,
      y: rect.bottom + 8 // Position below the element with 8px gap
    });
  }, []);

  const hideTooltip = useCallback(() => {
    // Clear any existing timeout
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
    }

    // Hide immediately
    hideTimeoutRef.current = setTimeout(() => {
      setTooltipData(null);
      hideTimeoutRef.current = null;
    }, 50); // Small delay to prevent flickering
  }, []);

  // Force immediate hide (for clicking elsewhere)
  const forceHideTooltip = useCallback(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    setTooltipData(null);
  }, []);

  return {
    tooltipData,
    tooltipPosition,
    showTooltip,
    hideTooltip,
    forceHideTooltip
  };
};

// Enhanced flag component using flag API with fallback
const CountryFlag = ({ countryCode, className = "w-6 h-4" }) => {
  const [imageError, setImageError] = useState(false);
  
  // Fallback emoji flags
  const getCountryFlagEmoji = (code) => {
    const flags = {
      'US': '🇺🇸', 'GB': '🇬🇧', 'DE': '🇩🇪', 'FR': '🇫🇷', 'IT': '🇮🇹',
      'ES': '🇪🇸', 'CA': '🇨🇦', 'AU': '🇦🇺', 'JP': '🇯🇵', 'KR': '🇰🇷',
      'CN': '🇨🇳', 'IN': '🇮🇳', 'BR': '🇧🇷', 'MX': '🇲🇽', 'AR': '🇦🇷',
      'CL': '🇨🇱', 'CO': '🇨🇴', 'PE': '🇵🇪', 'VE': '🇻🇪', 'RU': '🇷🇺',
      'PL': '🇵🇱', 'NL': '🇳🇱', 'BE': '🇧🇪', 'CH': '🇨🇭', 'AT': '🇦🇹',
      'SE': '🇸🇪', 'NO': '🇳🇴', 'DK': '🇩🇰', 'FI': '🇫🇮', 'PT': '🇵🇹',
      'GR': '🇬🇷', 'TR': '🇹🇷', 'ZA': '🇿🇦', 'EG': '🇪🇬', 'NG': '🇳🇬',
      'KE': '🇰🇪', 'MA': '🇲🇦', 'TH': '🇹🇭', 'VN': '🇻🇳', 'MY': '🇲🇾',
      'SG': '🇸🇬', 'ID': '🇮🇩', 'PH': '🇵🇭', 'NZ': '🇳🇿', 'IE': '🇮🇪',
      'IL': '🇮🇱', 'AE': '🇦🇪', 'SA': '🇸🇦', 'PK': '🇵🇰', 'BD': '🇧🇩',
      'LK': '🇱🇰', 'MM': '🇲🇲', 'KH': '🇰🇭', 'LA': '🇱🇦', 'UY': '🇺🇾',
      'PY': '🇵🇾', 'BO': '🇧🇴', 'EC': '🇪🇨', 'CZ': '🇨🇿', 'SK': '🇸🇰',
      'HU': '🇭🇺', 'RO': '🇷🇴', 'BG': '🇧🇬', 'HR': '🇭🇷', 'SI': '🇸🇮',
      'LT': '🇱🇹', 'LV': '🇱🇻', 'EE': '🇪🇪', 'IS': '🇮🇸', 'LU': '🇱🇺',
      'MT': '🇲🇹', 'CY': '🇨🇾', 'UA': '🇺🇦', 'BY': '🇧🇾', 'MD': '🇲🇩',
      'BA': '🇧🇦', 'RS': '🇷🇸', 'ME': '🇲🇪', 'MK': '🇲🇰', 'AL': '🇦🇱',
      'XK': '🇽🇰', 'AM': '🇦🇲', 'AZ': '🇦🇿', 'GE': '🇬🇪', 'KZ': '🇰🇿',
      'UZ': '🇺🇿', 'TM': '🇹🇲', 'KG': '🇰🇬', 'TJ': '🇹🇯', 'AF': '🇦🇫',
      'IR': '🇮🇷', 'IQ': '🇮🇶', 'SY': '🇸🇾', 'LB': '🇱🇧', 'JO': '🇯🇴',
      'PS': '🇵🇸', 'YE': '🇾🇪', 'OM': '🇴🇲', 'QA': '🇶🇦', 'BH': '🇧🇭',
      'KW': '🇰🇼', 'MN': '🇲🇳', 'NP': '🇳🇵', 'BT': '🇧🇹', 'MV': '🇲🇻'
    };
    return flags[countryCode] || '🌍';
  };

  if (imageError) {
    return (
      <span className="text-lg leading-none" title={`${countryCode} flag`}>
        {getCountryFlagEmoji(countryCode)}
      </span>
    );
  }

  return (
    <img
      src={`https://flagcdn.com/24x18/${countryCode.toLowerCase()}.png`}
      alt={`${countryCode} flag`}
      className={`${className} object-cover rounded-sm border border-gray-200 shadow-sm`}
      onError={() => setImageError(true)}
      title={`${countryCode} flag`}
      loading="lazy"
    />
  );
};

// Get country full name from code
const getCountryName = (countryCode) => {
  const countryNames = {
    'US': 'United States', 'GB': 'United Kingdom', 'DE': 'Germany', 'FR': 'France', 'IT': 'Italy',
    'ES': 'Spain', 'CA': 'Canada', 'AU': 'Australia', 'JP': 'Japan', 'KR': 'South Korea',
    'CN': 'China', 'IN': 'India', 'BR': 'Brazil', 'MX': 'Mexico', 'AR': 'Argentina',
    'CL': 'Chile', 'CO': 'Colombia', 'PE': 'Peru', 'VE': 'Venezuela', 'RU': 'Russia',
    'PL': 'Poland', 'NL': 'Netherlands', 'BE': 'Belgium', 'CH': 'Switzerland', 'AT': 'Austria',
    'SE': 'Sweden', 'NO': 'Norway', 'DK': 'Denmark', 'FI': 'Finland', 'PT': 'Portugal',
    'GR': 'Greece', 'TR': 'Turkey', 'ZA': 'South Africa', 'EG': 'Egypt', 'NG': 'Nigeria',
    'KE': 'Kenya', 'MA': 'Morocco', 'TH': 'Thailand', 'VN': 'Vietnam', 'MY': 'Malaysia',
    'SG': 'Singapore', 'ID': 'Indonesia', 'PH': 'Philippines', 'NZ': 'New Zealand', 'IE': 'Ireland',
    'IL': 'Israel', 'AE': 'UAE', 'SA': 'Saudi Arabia', 'PK': 'Pakistan', 'BD': 'Bangladesh',
    'LK': 'Sri Lanka', 'MM': 'Myanmar', 'KH': 'Cambodia', 'LA': 'Laos', 'UY': 'Uruguay',
    'PY': 'Paraguay', 'BO': 'Bolivia', 'EC': 'Ecuador', 'CZ': 'Czech Republic', 'SK': 'Slovakia',
    'HU': 'Hungary', 'RO': 'Romania', 'BG': 'Bulgaria', 'HR': 'Croatia', 'SI': 'Slovenia',
    'LT': 'Lithuania', 'LV': 'Latvia', 'EE': 'Estonia', 'IS': 'Iceland', 'LU': 'Luxembourg',
    'MT': 'Malta', 'CY': 'Cyprus', 'UA': 'Ukraine', 'BY': 'Belarus', 'MD': 'Moldova'
  };
  return countryNames[countryCode] || countryCode;
};

// Professional Tooltip component with flag API - Stable positioning
export const Tooltip = ({ data, position, formatNumber }) => {
  if (!data || !Array.isArray(data) || data.length === 0) return null;

  // Calculate total traffic
  const totalTraffic = data.reduce((sum, geo) => sum + parseInt(geo.count || 0), 0);

  // Show first 5 countries in detail, then count remaining
  const displayCount = 5;
  // Sort data by traffic count in descending order (highest traffic first)
  const sortedData = [...data].sort((a, b) => (parseInt(b.count || 0)) - (parseInt(a.count || 0)));
  const displayData = sortedData.slice(0, displayCount);
  const remainingCount = Math.max(0, sortedData.length - displayCount);

  // Calculate percentage for each country
  const displayDataWithPercentage = displayData.map(geo => ({
    ...geo,
    percentage: totalTraffic > 0 ? ((parseInt(geo.count || 0) / totalTraffic) * 100).toFixed(1) : 0
  }));

  // Smart positioning to avoid going off screen
  const tooltipWidth = 320;
  const margin = 16;

  // Calculate approximate tooltip height based on content
  const baseHeight = 100; // Header + total section
  const itemHeight = 52; // Each country row
  const remainingHeight = remainingCount > 0 ? 40 : 0;
  const tooltipHeight = baseHeight + (displayData.length * itemHeight) + remainingHeight;

  // Calculate if tooltip should appear above or below
  const shouldShowAbove = position.y + tooltipHeight + margin > window.innerHeight;

  // Calculate horizontal position - center on trigger element
  const leftPosition = Math.max(
    margin,
    Math.min(position.x - tooltipWidth / 2, window.innerWidth - tooltipWidth - margin)
  );

  // Calculate vertical position with proper spacing
  const topPosition = shouldShowAbove
    ? position.y - tooltipHeight - 16 // Above with gap
    : position.y; // Below (position already includes 8px gap from hook)

  return (
    <div
      className="fixed z-[9999] transition-opacity duration-150"
      style={{
        left: leftPosition,
        top: topPosition,
        pointerEvents: 'none',
        opacity: 1
      }}
    >
      {/* Tooltip Container */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-xl min-w-[300px] max-w-[320px] relative overflow-hidden">

        {/* Tooltip Arrow - Dynamic positioning */}
        <div
          className={`absolute ${shouldShowAbove ? '-bottom-2' : '-top-2'} w-4 h-4 bg-white border-gray-200 transform rotate-45 ${
            shouldShowAbove
              ? 'border-r border-b'
              : 'border-l border-t'
          }`}
          style={{
            left: Math.min(Math.max(position.x - leftPosition - 8, 16), tooltipWidth - 32)
          }}
        />

        {/* Header */}
        <div className="px-4 py-3 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-gray-50">
          <h3 className="text-sm font-semibold text-gray-800">Geographic Traffic Distribution</h3>
          <p className="text-xs text-gray-500 mt-0.5">{data.length} countries</p>
        </div>

        {/* Countries List */}
        <div className="p-3 max-h-[280px] overflow-y-auto">
          <div className="space-y-1">
            {displayDataWithPercentage.map((geo, index) => (
              <div key={`${geo.country}-${index}`} className="flex items-center justify-between py-2 px-2 rounded-lg hover:bg-gray-50 transition-colors">
                <div className="flex items-center space-x-3">
                  {/* Use CountryFlag component with API */}
                  <CountryFlag countryCode={geo.country} className="w-6 h-4" />
                  <div>
                    <div className="text-sm font-medium text-gray-800">{getCountryName(geo.country)}</div>
                    <div className="text-xs text-gray-400">{geo.country}</div>
                  </div>
                </div>

                <div className="text-right">
                  <div className="text-sm font-semibold text-blue-600">{formatNumber(geo.count)}</div>
                  <div className="text-xs text-gray-400">{geo.percentage}%</div>
                </div>
              </div>
            ))}
          </div>

          {/* Remaining countries indicator */}
          {remainingCount > 0 && (
            <div className="mt-2 pt-2 border-t border-gray-100">
              <div className="text-center text-xs text-gray-500 py-1">
                +{remainingCount} more {remainingCount === 1 ? 'country' : 'countries'}
              </div>
            </div>
          )}
        </div>

        {/* Total at the bottom - Fixed footer */}
        <div className="px-4 py-3 border-t border-gray-100 bg-gray-50">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-gray-700">Total Traffic</span>
            <span className="text-sm font-bold text-blue-600">{formatNumber(totalTraffic)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Demo Component showing the flag integration
const TooltipDemo = () => {
  const { tooltipData, tooltipPosition, showTooltip, hideTooltip } = useTooltip();
  
  const formatNumber = (num) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num?.toLocaleString() || '0';
  };

  const mockGeoData = [
    { country: 'US', count: '45000' },
    { country: 'GB', count: '23000' },
    { country: 'DE', count: '18000' },
    { country: 'FR', count: '12000' },
    { country: 'CA', count: '8500' },
    { country: 'AU', count: '7200' },
    { country: 'JP', count: '5800' },
    { country: 'IN', count: '4200' }
  ];

  const handleMouseEnter = (event) => {
    showTooltip(mockGeoData, event);
  };

  const handleMouseLeave = () => {
    hideTooltip();
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-semibold text-gray-900 mb-2">Enhanced Tooltip with Flag API</h1>
          <p className="text-gray-600">Now showing real flag images with emoji fallback</p>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm p-8 border border-gray-200">
          <div className="text-center">
            <button
              className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md shadow-sm transition-colors"
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <span className="mr-2">🌍</span>
              View Geographic Data with Flag API
            </button>
            
            <div className="mt-8 text-left">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Traffic Overview</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {mockGeoData.slice(0, 4).map((geo, index) => (
                  <div key={index} className="p-3 border border-gray-200 rounded-md text-center">
                    <div className="mb-2 flex justify-center">
                      <CountryFlag countryCode={geo.country} className="w-8 h-6" />
                    </div>
                    <div className="text-sm font-medium text-gray-700">{getCountryName(geo.country)}</div>
                    <div className="text-sm text-blue-600 font-semibold">{formatNumber(geo.count)}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        
        <Tooltip 
          data={tooltipData} 
          position={tooltipPosition} 
          formatNumber={formatNumber} 
        />
      </div>
    </div>
  );
};

export default TooltipDemo;